// components/student/StudentSidebar.js
import React from "react";
import { Link, useLocation } from "react-router-dom";
import "./../../../assets/css/student/Sidebar.css";

/* ════════════════════════════════════════
   ICONS  — Lucide-style, 20×20, stroke 1.75
   ════════════════════════════════════════ */
const Icon = ({ d, children, viewBox = "0 0 24 24" }) => (
  <svg width="18" height="18" viewBox={viewBox} fill="none"
    stroke="currentColor" strokeWidth="1.75"
    strokeLinecap="round" strokeLinejoin="round">
    {d ? <path d={d} /> : children}
  </svg>
);

const Icons = {
  Dashboard: () => (
    <Icon>
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </Icon>
  ),
  Courses: () => (
    <Icon>
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </Icon>
  ),
  Wishlist: () => (
    <Icon>
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </Icon>
  ),
  Roadmap: () => (
    <Icon>
      <circle cx="5" cy="6" r="2" />
      <circle cx="19" cy="6" r="2" />
      <circle cx="12" cy="18" r="2" />
      <path d="M7 6h10" />
      <path d="M5 8v4a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8" />
      <path d="M12 14v2" />
    </Icon>
  ),
  Assignment: () => (
    <Icon>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <line x1="10" y1="9" x2="8" y2="9" />
    </Icon>
  ),
  Code: () => (
    <Icon>
      <polyline points="16 18 22 12 16 6" />
      <polyline points="8 6 2 12 8 18" />
    </Icon>
  ),
  AskAI: () => (
    <Icon>
      <path d="M12 2L9.5 9.5 2 12l7.5 2.5L12 22l2.5-7.5L22 12l-7.5-2.5z" />
    </Icon>
  ),
  Mentors: () => (
    <Icon>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </Icon>
  ),
  MentorReq: () => (
    <Icon>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <line x1="19" y1="8" x2="19" y2="14" />
      <line x1="22" y1="11" x2="16" y2="11" />
    </Icon>
  ),
  Chat: () => (
    <Icon>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </Icon>
  ),
  Payment: () => (
    <Icon>
      <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
      <line x1="1" y1="10" x2="23" y2="10" />
    </Icon>
  ),
  Report: () => (
    <Icon>
      <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
      <line x1="4" y1="22" x2="4" y2="15" />
    </Icon>
  ),
  Leaderboard: () => (
    <Icon>
      <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
    </Icon>
  ),
  HelpSupport: () => (
    <Icon>
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </Icon>
  ),
  Logout: () => (
    <Icon>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </Icon>
  ),
  ChevronLeft: ({ flipped }) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"
      style={{ transform: flipped ? "rotate(180deg)" : "none", transition: "transform 0.3s ease" }}>
      <polyline points="15 18 9 12 15 6" />
    </svg>
  ),
};

/* ════════════════════════════════════════
   NAV CONFIG
   ════════════════════════════════════════ */
export const NAV_ITEMS = [
  {
    section: "Overview",
    items: [
      { path: "/student/dashboard", Icon: Icons.Dashboard, label: "Dashboard" },
    ],
  },
  {
    section: "Discover",
    items: [
      { path: "/student/my-courses",        Icon: Icons.Courses,    label: "My Courses" },
      { path: "/student/wishlist",           Icon: Icons.Wishlist,   label: "Wishlist" },
      { path: "/student/roadmaps-progress",  Icon: Icons.Roadmap,    label: "Roadmaps" },
    ],
  },
  {
    section: "Learning",
    items: [
      { path: "/student/my-assignments", Icon: Icons.Assignment, label: "Assignments" },
    ],
  },
  {
    section: "Practice",
    items: [
      { path: "/student/code-challenges", Icon: Icons.Code, label: "Code Arena" },
    ],
  },
  {
    section: "Support",
    items: [
      { path: "/student/ask-ai",          Icon: Icons.AskAI,     label: "Ask AI" },
      { path: "/student/my-mentor",       Icon: Icons.Mentors,   label: "My Mentors" },
      { path: "/student/mentor-requests", Icon: Icons.MentorReq, label: "Mentor Requests" },
      { path: "/student/Chat",            Icon: Icons.Chat,       label: "Chat" },
    ],
  },
  {
    section: "Management",
    items: [
      { path: "/student/payments",      Icon: Icons.Payment, label: "Payments" },
      { path: "/student/report-issues", Icon: Icons.Report,  label: "Report Issue" },
    ],
  },
  {
    section: "Gamification",
    items: [
      { path: "/student/leaderboard", Icon: Icons.Leaderboard, label: "Leaderboard" },
    ],
  },
  {
    section: "Help",
    items: [
      { path: "/student/help-support", Icon: Icons.HelpSupport, label: "Help & Support" },
    ],
  },
];

/* ════════════════════════════════════════
   SIDEBAR
   ════════════════════════════════════════ */
function Sidebar({ studentName, studentAvatar, studentRole, studentEmail, collapsed, onToggle }) {
  const location    = useLocation();
  const isActive    = (path) => location.pathname === path;
  const avatarLetter = studentName ? studentName[0].toUpperCase() : "S";

  return (
    <aside className={`sb${collapsed ? " sb--collapsed" : ""}`}>

      {/* ── Brand / Toggle ── */}
      <div className="sb__header">
        {!collapsed && (
          <span className="sb__brand">TechMentor Hub</span>
        )}
        <button
          type="button"
          className="sb__toggle"
          onClick={onToggle}
          aria-label="Toggle sidebar"
        >
          <Icons.ChevronLeft flipped={collapsed} />
        </button>
      </div>

      {/* ── Profile ── */}
      <div className="sb__profile">
        <div className="sb__avatar">
          {studentAvatar
            ? <img src={studentAvatar} alt="avatar" className="sb__avatar-img" />
            : <span className="sb__avatar-letter">{avatarLetter}</span>
          }
          <span className="sb__online" />
        </div>
        {!collapsed && (
          <div className="sb__profile-info">
            <span className="sb__profile-name">{studentName || "Student"}</span>
            <span className="sb__profile-role" title={studentEmail}>
              {studentEmail || studentRole || "Student"}
            </span>
          </div>
        )}
      </div>

      <div className="sb__divider" />

      {/* ── Navigation ── */}
      <nav className="sb__nav">
        {NAV_ITEMS.map(({ section, items }) => (
          <div key={section} className="sb__group">
            {!collapsed && (
              <span className="sb__section-label">{section}</span>
            )}
            {items.map(({ path, Icon, label, badge }, i) => (
              <Link
                key={path}
                to={path}
                className={`sb__item${isActive(path) ? " sb__item--active" : ""}`}
                style={{ animationDelay: `${i * 30}ms` }}
                title={collapsed ? label : undefined}
              >
                {isActive(path) && <span className="sb__active-bar" />}
                <span className="sb__item-icon"><Icon /></span>
                {!collapsed && <span className="sb__item-label">{label}</span>}
                {!collapsed && badge && (
                  <span className={`sb__badge${badge === "NEW" ? " sb__badge--new" : ""}`}>{badge}</span>
                )}
                {collapsed && badge && <span className="sb__badge-dot" />}
              </Link>
            ))}
          </div>
        ))}
      </nav>

      {/* ── Footer ── */}
      <div className="sb__footer">
        <button
          type="button"
          className="sb__logout"
          onClick={() => { window.location.href = "http://localhost:3001"; }}
          title={collapsed ? "Logout" : undefined}
        >
          <span className="sb__item-icon"><Icons.Logout /></span>
          {!collapsed && <span className="sb__item-label">Logout</span>}
        </button>
      </div>

    </aside>
  );
}

export default Sidebar;